import { Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AttachmentService } from 'src/app/services/attachment.service';
import { EnvironmentService } from 'src/app/services/environment.service';
import { OverlayService } from 'src/app/services/overlay.service';
import { RssService } from 'src/app/services/rss.service';
import { ImageGalleryComponent } from '../../../../shared/image-gallery/image-gallery.component';

@Component({
  selector: 'app-edit-rss-feed',
  templateUrl: './edit-rss-feed.component.html',
  styleUrls: ['./edit-rss-feed.component.scss']
})
export class EditRssFeedComponent implements OnInit {
  /**
   * The Form to edit the Feed
   */
  addForm: UntypedFormGroup;
  /**
   * The Array of Images to be uploaded
   */
  files: File[] = [];
  /**
   * The current RSS-Feed
   */
  currentRss;
  /**
   * The Options of the Dropdown to select the Type of the Feed
   */
  public stateOptions = [
    { value: 'NEWS', viewValue: 'Nachricht' },
    { value: 'APPOINTMENT', viewValue: 'Veranstaltung' },
  ]
  /**
   * Is the Page finished loading?
   */
  finishedLoading: boolean = false;
  /**
   * Should the Image be uploaded to the Backend
   */
  uploadImage: boolean = false;
  /**
   * The Data to be uploaded
   */
  imageProperties = [
    { key: 'logo', value: null, hint: 'Format: 800 x 500, Typ: .png', hasChanged: false },
  ];
  /**
   * constructor
   * Init the Form
   * @param fb 
   * @param overlayService 
   * @param router 
   * @param route 
   * @param rss 
   * @param dialog 
   * @param attachmentService 
   * @param env 
   */
  constructor(
    private fb: UntypedFormBuilder,
    private overlayService: OverlayService,
    private router: Router,
    private route: ActivatedRoute,
    private rss: RssService,
    private dialog: MatDialog,
    private attachmentService: AttachmentService,
    private env: EnvironmentService) {
    this.addForm = this.fb.group({
      feedUri: ['', [Validators.required, EditRssFeedComponent.urlValidator]],
      type: ['', [Validators.required]],
      autoFetch: ['', []],
      username: ['', []],
      password: ['', []],
      hasUsername: ['', []],
      image: ['', [Validators.required]]
    })
  }
/**
 * ngOnInit
 * Get the Current Feed via the prameter from the URL
 */
  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.rss.getRssFeed(params['id']).subscribe(
        (currentRSS: any) => {
          this.currentRss = currentRSS;
          this.addForm.patchValue(currentRSS);
          this.addForm.patchValue({
            image: currentRSS.defaultImage
          })
          if (currentRSS.password != '') {
            this.addForm.patchValue({
              hasUsername: true
            })
          }
          this.finishedLoading = true;
        },
        error => {
          console.log("error: ", error);
          this.overlayService.openErrorDialog("Fehler beim Laden", "Versuchen Sie es bitte später noch einmal. Sollte das Problem nicht in absehbarer Zeit behoben sein, schreiben Sie uns eine E-Mail an: support@plusservices.de", "Zurück")
        }
      );
    })
  }
/**
 * Add the File from the Array of files to be uploaded
 * @param event 
 */
  onSelect(event) {
    this.files.push(...event.addedFiles);
    this.addForm.controls["image"].setValue(this.files[0])
  }
/**
 * Remove the File from the Array of files to be uploaded
 * @param event 
 */
  onRemove(event) {
    this.files.splice(this.files.indexOf(event), 1);
  }
  /**
   * Save the Changes to the Feed
   * @returns 
   */
  editRSSFeed() {
    this.addForm.markAllAsTouched();
    if (this.addForm.invalid) {
      return;
    }

    if (this.files.length != 0) {
      this.attachmentService.createAttachment(this.env.getClientId(), this.files[0]).subscribe(attachment => {
        this.addForm.controls.image.setValue(attachment);
        this.currentRss.feedUri = this.addForm.value.feedUri;
        this.currentRss.autoFetch = this.addForm.value.autoFetch;
        this.currentRss.username = this.addForm.value.username;
        this.currentRss.password = this.addForm.value.password;
        this.currentRss.type = this.addForm.value.type;
        this.currentRss.defaultImage = attachment;
        this.rss.editRssFeed(this.currentRss).subscribe(fromEdit => {
          this.overlayService.openSnackBar("RSS-Feed erfolgreich bearbeitet!");
          this.router.navigate(["caseworker/rssFeed"]);
        })
      })
    } else {
      this.currentRss.feedUri = this.addForm.value.feedUri;
      this.currentRss.autoFetch = this.addForm.value.autoFetch;
      this.currentRss.username = this.addForm.value.username;
      this.currentRss.password = this.addForm.value.password;
      this.currentRss.type = this.addForm.value.type;
      this.currentRss.defaultImage = this.addForm.value.image;
      this.rss.editRssFeed(this.currentRss).subscribe(fromEdit => {
        this.overlayService.openSnackBar("RSS-Feed erfolgreich bearbeitet!");
        this.router.navigate(["caseworker/rssFeed"]);
      })
    }
  }
  /**
   * Open the Gallery to select an Image for the Feed
   * @param property 
   */
  openImageGallery(property) {
    const dialogRef = this.dialog.open(ImageGalleryComponent);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.addForm.controls.image.setValue(result);
        property.value = result.url;
        property.hasChanged = true;
        property.newValue = `${result.clientRef}/attachment/${result.publicId}`;
        this.uploadImage = false;
      }
    });
  }
  /**
   * Remove the current Image from the Form
   */
  resetImage() {
    this.addForm.controls.image.reset();
    this.uploadImage = true;
  }

  /**
   * Check if the URL passedin is valid
   */
  private static urlValidator({ value }: AbstractControl): null | ValidationErrors {
    try {
      new URL(value);
      return null;
    } catch {
      return { pattern: true };
    }
  }
}
